import { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../components/BottomActionSheet";
import { lockedActions } from "../../../../components/SettlementLock";
import { getCurrencySymbol, getNumberFormat } from "../../../../utils";
import { AccountChip } from "./AccountChip";
import { KebabButton } from "../../../../components/KebabButton";
import {
  Card,
  CardAmount,
  CardCategoryBlock,
  CardCategoryName,
  CardMiddle,
  CardNote,
  CardSubCategory,
  CardTop,
  CardTopLeft,
  MenuWrapper,
} from "./styles";
import { TypePill } from "./TypePill";
import { MobileCardProps } from "./types";
import {
  formatRowDate,
  getAmountSign,
  getAmountVariant,
  getPrimaryAccountId,
  getPrimaryAccountName,
  getSignedAmount,
  getTypeLabel,
} from "./utils";

export const MobileCard: React.FC<MobileCardProps> = ({
  transaction,
  accountIdsByName,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const accountName = getPrimaryAccountName(transaction);
  const accountId = getPrimaryAccountId(transaction);
  const variant = getAmountVariant(transaction.transactionType);
  const signedAmount = getSignedAmount(transaction);
  const symbol = getCurrencySymbol(transaction.currencyCode);
  const sign = getAmountSign(variant, transaction.amount);
  const amountLabel = `${sign}${getNumberFormat(Math.abs(signedAmount))} ${symbol}`;
  const typeLabel = getTypeLabel(transaction.transactionType);

  const sheetTitle = transaction.categoryName ?? typeLabel;
  const sheetSubtitleParts = [
    transaction.subCategoryName,
    formatRowDate(transaction.transactionDate),
  ].filter(Boolean);
  const sheetSubtitle = sheetSubtitleParts.join(" · ");

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const runAction = useCallback(
    (action: () => void) => {
      closeSheet();
      action();
    },
    [closeSheet],
  );

  const handleEdit = useCallback(() => {
    runAction(() => onEdit(transaction));
  }, [onEdit, runAction, transaction]);

  const handleDuplicate = useCallback(() => {
    runAction(() => onDuplicate(transaction));
  }, [onDuplicate, runAction, transaction]);

  const handleDelete = useCallback(() => {
    runAction(() => onDelete(transaction));
  }, [onDelete, runAction, transaction]);

  const { lock, actions: menu } = lockedActions({
    settlement: transaction.settlement,
    cashbackExpenseId: transaction.cashbackExpenseId,
    actions: (
      <MenuWrapper>
        <KebabButton
          size="small"
          onClick={openSheet}
          aria-label="Transaction options"
          aria-haspopup="dialog"
        />
      </MenuWrapper>
    ),
  });

  return (
    <Card data-testid="transaction-row">
      <CardTop>
        <CardTopLeft>
          <TypePill variant={variant} label={typeLabel} />
          {accountName && (
            <AccountChip
              name={accountName}
              accountId={accountId}
              accountIdsByName={accountIdsByName}
            />
          )}
        </CardTopLeft>
        {menu}
      </CardTop>
      <CardMiddle>
        <CardCategoryBlock>
          <CardCategoryName>{transaction.categoryName}</CardCategoryName>
          {transaction.subCategoryName && (
            <CardSubCategory>{transaction.subCategoryName}</CardSubCategory>
          )}
        </CardCategoryBlock>
        <CardAmount $variant={variant}>{amountLabel}</CardAmount>
      </CardMiddle>
      {transaction.note && <CardNote>{transaction.note}</CardNote>}
      {lock}
      <BottomActionSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        ariaLabel="Transaction actions"
      >
        <SheetCard>
          <SheetHeader>
            <HeaderInfo>
              <HeaderTitle>{sheetTitle}</HeaderTitle>
              {sheetSubtitle && (
                <HeaderSubtitle>{sheetSubtitle}</HeaderSubtitle>
              )}
            </HeaderInfo>
          </SheetHeader>
          <ActionButton type="button" onClick={handleEdit}>
            Edit
          </ActionButton>
          <ActionButton type="button" onClick={handleDuplicate}>
            Duplicate
          </ActionButton>
          <ActionButton
            type="button"
            $variant="destructive"
            onClick={handleDelete}
          >
            Delete
          </ActionButton>
        </SheetCard>
        <SheetCard>
          <CancelButton type="button" onClick={closeSheet}>
            Cancel
          </CancelButton>
        </SheetCard>
      </BottomActionSheet>
    </Card>
  );
};
